/**
 * "A newer version is ready."
 *
 * Once a service worker is installed, the page a reader sees is the one the
 * worker had on hand, and it stays that way until the worker is replaced. That
 * is exactly what makes offline reading work, and it is also how a fixed bug
 * keeps showing up for a week on the one device that never closed the tab.
 *
 * So the update is neither forced nor silent: the worker downloads it, this
 * bar says it is there, and the reader decides when to take it. Nothing is
 * reloaded under someone who is mid-chapter with a narration running.
 */

import { useEffect, useState } from "react";

import { RefreshIcon } from "./icons";
import { t, useUiLang } from "../i18n";
import { applyUpdate, onUpdateAvailable } from "../lib/offline";

export function UpdateBanner() {
  useUiLang();
  const [ready, setReady] = useState(false);

  useEffect(() => onUpdateAvailable(() => setReady(true)), []);

  if (!ready) return null;

  return (
    <div
      role="status"
      className={
        // Bottom, not top: the reader's header is already full, and on a phone
        // the bottom is the half a thumb reaches without moving the hand.
        "fixed inset-x-0 bottom-0 z-50 flex items-center justify-center gap-3 px-3 pb-[env(safe-area-inset-bottom)]"
      }
    >
      <div
        className={
          "mb-3 flex items-center gap-3 rounded-full bg-[var(--color-raised)] py-2 pl-4 pr-2 " +
          "shadow-2xl shadow-black/50 ring-1 ring-[var(--color-line)]"
        }
      >
        <span className="text-xs text-[var(--color-ink)]">{t("updateReady")}</span>
        <button
          onClick={() => void applyUpdate()}
          className={
            "tap flex items-center gap-1.5 rounded-full bg-[var(--color-accent-soft)] px-3 py-1.5 " +
            "text-xs text-[var(--color-accent)] transition-colors duration-150"
          }
        >
          <RefreshIcon className="h-4 w-4" />
          {t("updateApply")}
        </button>
        <button
          onClick={() => setReady(false)}
          className="tap rounded-full px-3 py-1.5 text-xs text-[var(--color-faint)] hover:text-[var(--color-muted)]"
        >
          {t("updateLater")}
        </button>
      </div>
    </div>
  );
}
